import React, {useEffect, useRef, useState} from "react";
import {Card, Input, Button, Link, Loading} from "@nextui-org/react";
import {MDBContainer, MDBRow, MDBCol, MDBCardBody} from "mdbreact";
import shopped from "../images/shopped.png";
import {Form} from "antd";
import Firebase from "../Firebase/Firebase";
import {getAuth, signInWithEmailAndPassword, signOut} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import {useNavigate} from "react-router-dom";
import {doc, getDoc, getFirestore} from "firebase/firestore";
import {useToast} from "@chakra-ui/react";
import {getFunctions, httpsCallable} from "firebase/functions";
import {logAdminAction} from "../utils/auditLog";
import {Turnstile} from "@marsidev/react-turnstile";
import type {TurnstileInstance} from "@marsidev/react-turnstile";

const firebaseAuth = getAuth(Firebase);
const db           = getFirestore(Firebase);
const functions    = getFunctions(Firebase);

const sendAdminLoginOtp    = httpsCallable(functions, 'sendAdminLoginOtp');
const verifyAdminLoginOtp  = httpsCallable(functions, 'verifyAdminLoginOtp');
const verifyTurnstileToken = httpsCallable(functions, 'verifyTurnstileToken');

const TURNSTILE_SITE_KEY = '0x4AAAAAADezLi83Sen4p0Ru';

type Step = 'credentials' | 'otp';

const isOfflineError = (e: any): boolean => {
    const msg = (e?.message ?? '').toLowerCase();
    return e?.code === 'unavailable' || e?.code === 'failed-precondition' || msg.includes('offline');
};

const isAdminUser = (data: any): boolean => {
    const hasAdminFlag = (data?.userRole ?? '').toLowerCase() === 'admin' || data?.isAdmin === true;
    const isDisabled   = (data?.userStatus ?? '').toLowerCase() === 'disabled';
    return hasAdminFlag && !isDisabled;
};

// ── Shared card wrapper ──────────────────────────────────────────────────────
// Must live at module level: declared inside Login, it was a new component
// type on every render, so React remounted everything in it — including the
// Turnstile widget, which restarted its challenge after each success (the
// endless "Verifying…" spinner) and made the inputs lose focus.
const Shell = ({children}: {children: React.ReactNode}) => (
    <div className="vh-100 bg">
        <MDBContainer>
            <MDBRow justify="center" align="center" center>
                <MDBCol md="12" className="my-5">
                    <Card isHoverable className="my-5 p-2" style={{width:'25rem'}}>
                        <MDBCardBody>
                            <img src={shopped} style={{width:'15rem', height:'3.5rem'}}
                                 className="rounded mx-auto mb-4 d-block" alt="Shopped"/>
                            {children}
                        </MDBCardBody>
                    </Card>
                </MDBCol>
            </MDBRow>
        </MDBContainer>
    </div>
);

const Login = () => {
    const navigate  = useNavigate();
    const toast     = useToast();
    const [user]    = useAuthState(firebaseAuth);

    const [step, setStep]                     = useState<Step>('credentials');
    const [loading, setLoading]               = useState(false);
    const [resending, setResending]           = useState(false);
    const [error, setError]                   = useState('');
    const [adminEmail, setAdminEmail]         = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    const [tsStatus, setTsStatus]             = useState<'loading'|'verified'|'error'>('loading');
    const turnstileRef                        = useRef<TurnstileInstance>(null);
    const [otpForm]                           = Form.useForm();
    const [credForm]                          = Form.useForm();

    const resetTurnstile = () => {
        setTurnstileToken('');
        setTsStatus('loading');
        turnstileRef.current?.reset();
    };

    // Auto-advance if the user session was already verified (e.g. page refresh
    // after a completed 2FA). We track this with sessionStorage so a hard
    // refresh forces the full 2FA again.
    const otpVerifiedKey = `otp_verified_${user?.uid}`;

    useEffect(() => {
        if (!user) return;
        if (sessionStorage.getItem(otpVerifiedKey) === 'true') {
            checkAdminAndNavigate();
        }
        // If we're on the OTP step, stay there — don't auto-redirect.
    }, [user]);

    const checkAdminAndNavigate = async () => {
        try {
            const snap = await getDoc(doc(db, 'users', user!.uid));
            if (snap.exists() && isAdminUser(snap.data())) {
                await logAdminAction('admin_login', {note: 'Session restored'});
                navigate('/home');
            } else {
                await signOut(firebaseAuth);
                setError('Invalid credentials or account access revoked.');
                setStep('credentials');
            }
        } catch (e: any) {
            if (isOfflineError(e)) {
                setError('No internet connection. Please check your network and try again.');
            } else {
                await signOut(firebaseAuth);
                setError('Session could not be verified. Please log in again.');
                sessionStorage.removeItem(otpVerifiedKey);
                setStep('credentials');
            }
        }
    };

    // ── Step 1: email + password ────────────────────────────────────────────
    const handleLogin = async (values: any) => {
        if (!turnstileToken) {
            setError('Please complete the security challenge.');
            return;
        }
        setLoading(true);
        setError('');

        // ── 1a. Turnstile bot-check ─────────────────────────────────────────
        try {
            await verifyTurnstileToken({token: turnstileToken});
        } catch {
            resetTurnstile();
            setLoading(false);
            setError('Security challenge failed. Please try again.');
            return;
        }

        // ── 1b. Firebase Auth sign-in ───────────────────────────────────────
        let cred: any;
        try {
            cred = await signInWithEmailAndPassword(
                firebaseAuth, values.email, values.password
            );
        } catch (err: any) {
            setLoading(false);
            const code = err?.code ?? '';
            const AUTH_ERRORS: Record<string, string> = {
                'auth/invalid-credential':        'Incorrect email or password.',
                'auth/invalid-login-credentials': 'Incorrect email or password.',
                'auth/user-not-found':            'Incorrect email or password.',
                'auth/wrong-password':            'Incorrect email or password.',
                'auth/too-many-requests':         'Too many failed attempts. Try again later or reset your password.',
                'auth/user-disabled':             'This account has been disabled.',
                'auth/invalid-email':             'Please enter a valid email address.',
                'auth/network-request-failed':    'Network error. Check your connection and try again.',
            };
            setError(
                isOfflineError(err)
                    ? 'No internet connection. Please check your network and try again.'
                    : (AUTH_ERRORS[code] ?? 'Incorrect email or password.')
            );
            resetTurnstile();
            return;
        }

        // ── 1b. Verify admin status in Firestore ────────────────────────────
        try {
            const snap = await getDoc(doc(db, 'users', cred.user.uid));
            if (!snap.exists() || !isAdminUser(snap.data())) {
                await signOut(firebaseAuth);
                setError('This account does not have admin access.');
                setLoading(false);
                resetTurnstile();
                return;
            }
        } catch (e: any) {
            await signOut(firebaseAuth).catch(() => {});
            setLoading(false);
            setError(isOfflineError(e)
                ? 'No internet connection. Please check your network and try again.'
                : 'Could not verify account. Please try again.');
            resetTurnstile();
            return;
        }

        // ── 1c. Send email OTP ──────────────────────────────────────────────
        setAdminEmail(values.email);
        try {
            await sendAdminLoginOtp({});
            setStep('otp');
        } catch (err: any) {
            await signOut(firebaseAuth).catch(() => {});
            const msg = err?.message ?? '';
            setError(
                msg.includes('60 seconds')
                    ? 'A code was already sent. Please wait 60 seconds before requesting a new one.'
                    : 'Could not send verification code. Please try again or use "Forgot password".'
            );
            resetTurnstile();
        } finally {
            setLoading(false);
        }
    };

    // ── Step 2: OTP verify ──────────────────────────────────────────────────
    const handleOtp = async (values: any) => {
        setLoading(true);
        setError('');
        try {
            await verifyAdminLoginOtp({otp: values.otp.trim()});
            sessionStorage.setItem(otpVerifiedKey, 'true');
            await logAdminAction('admin_login', {note: 'Login via email + OTP 2FA'});
            navigate('/home');
        } catch (err: any) {
            const msg = err?.message ?? 'Verification failed.';
            setError(msg);
            // If OTP exhausted or expired, go back to start
            if (err?.code === 'functions/resource-exhausted' ||
                err?.code === 'functions/deadline-exceeded') {
                await signOut(firebaseAuth);
                setStep('credentials');
                otpForm.resetFields();
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        setError('');
        try {
            await sendAdminLoginOtp({});
            toast({position:'top', title:'New code sent', status:'success',
                   duration:3000, isClosable:true});
            otpForm.resetFields();
        } catch (err: any) {
            setError(err?.message ?? 'Could not resend code.');
        } finally {
            setResending(false);
        }
    };

    const handleBack = async () => {
        await signOut(firebaseAuth);
        setStep('credentials');
        setError('');
        otpForm.resetFields();
    };

    // ── Step 1 UI ───────────────────────────────────────────────────────────
    if (step === 'credentials') {
        return (
            <Shell>
                <Form form={credForm} onFinish={handleLogin}>
                    <Form.Item name="email"
                        rules={[
                            {required:true, message:'Please enter your email'},
                            {type:'email',  message:'Not a valid email'},
                        ]}>
                        <Input type="email" size="lg" width="100%" onChange={() => {}}
                               placeholder="Admin email address" className="my-1"/>
                    </Form.Item>
                    <Form.Item name="password"
                        rules={[{required:true, message:'Please enter your password'}]}>
                        <Input.Password size="lg" width="100%" onChange={() => {}}
                                        placeholder="Password" className="my-1"/>
                    </Form.Item>

                    <div style={{display:'flex', flexDirection:'column', alignItems:'center', margin:'8px 0', gap:6}}>
                        <Turnstile
                            ref={turnstileRef}
                            siteKey={TURNSTILE_SITE_KEY}
                            onSuccess={(t) => { setTurnstileToken(t); setTsStatus('verified'); }}
                            onExpire={() => { setTurnstileToken(''); setTsStatus('loading'); }}
                            onError={() => { setTurnstileToken(''); setTsStatus('error'); }}
                            options={{appearance: 'always'}}
                        />
                        {tsStatus === 'verified' && (
                            <span style={{fontSize:12, color:'#059669'}}>✓ Verified</span>
                        )}
                        {tsStatus === 'error' && (
                            <button type="button"
                                onClick={() => turnstileRef.current?.reset()}
                                style={{fontSize:12, color:'#29327b', background:'none', border:'none',
                                        cursor:'pointer', textDecoration:'underline', padding:0}}>
                                Challenge failed — click to retry
                            </button>
                        )}
                    </div>

                    {error && (
                        <div style={{background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8,
                                     padding:'8px 12px', fontSize:13, color:'#dc2626', marginBottom:12}}>
                            {error}
                        </div>
                    )}

                    <Button shadow disabled={loading || !turnstileToken} type="submit"
                            className="w-100 bg-indigo-500 my-3">
                        {loading
                            ? <Loading type="spinner" color="currentColor" size="sm"/>
                            : 'Continue'}
                    </Button>
                </Form>
                <div className="text-center text-black-50 d-flex justify-content-center" style={{fontSize:13}}>
                    Forgot password? &nbsp;<Link href="/forgot">Reset it</Link>
                </div>
            </Shell>
        );
    }

    // ── Step 2 UI ───────────────────────────────────────────────────────────
    return (
        <Shell>
            <div style={{textAlign:'center', marginBottom:16}}>
                <div style={{fontSize:28, marginBottom:8}}>✉️</div>
                <p style={{fontWeight:700, fontSize:15, color:'#1a1a2e', margin:'0 0 4px'}}>
                    Check your email
                </p>
                <p style={{fontSize:13, color:'#666', margin:0}}>
                    We sent a 6-digit code to<br/>
                    <b>{adminEmail}</b>
                </p>
            </div>

            <Form form={otpForm} onFinish={handleOtp}>
                <Form.Item name="otp"
                    rules={[
                        {required:true, message:'Please enter the code'},
                        {pattern:/^\d{6}$/, message:'Must be 6 digits'},
                    ]}>
                    <Input
                        size="lg"
                        width="100%"
                        onChange={() => {}}
                        placeholder="6-digit code"
                        className="my-1"
                        css={{textAlign:'center', letterSpacing:'0.3em', fontSize:'1.2em'}}
                    />
                </Form.Item>

                {error && (
                    <div style={{background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8,
                                 padding:'8px 12px', fontSize:13, color:'#dc2626', marginBottom:12}}>
                        {error}
                    </div>
                )}

                <Button shadow disabled={loading} type="submit"
                        className="w-100 bg-indigo-500 my-3">
                    {loading
                        ? <Loading type="spinner" color="currentColor" size="sm"/>
                        : 'Verify & Sign In'}
                </Button>
            </Form>

            <div style={{display:'flex', justifyContent:'space-between', fontSize:13, marginTop:4}}>
                <button onClick={handleBack} disabled={loading}
                        style={{background:'none', border:'none', color:'#888',
                                cursor:'pointer', padding:0, fontSize:13}}>
                    ← Back
                </button>
                <button onClick={handleResend} disabled={resending || loading}
                        style={{background:'none', border:'none', color:'#29327b',
                                cursor:'pointer', padding:0, fontSize:13, fontWeight:600}}>
                    {resending ? 'Sending…' : 'Resend code'}
                </button>
            </div>

            <p style={{textAlign:'center', fontSize:11, color:'#aaa', marginTop:16}}>
                Code expires in 5 minutes
            </p>
        </Shell>
    );
};

export default Login;
